import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase, Business, Tenant } from '../lib/supabase';
import { getCachedBusiness, cachePublicBusiness } from '../lib/offline/publicDiscoveryCache';

interface TenantContextType {
  tenant: Business | null;
  countryTenant: Tenant | null;
  tenantSlug: string | null;
  isTenant: boolean;
  isStorefront: boolean; // Keep for backward compatibility
  loading: boolean;
  urlPrefix: string;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initLoading = () => {
    try {
      const hostname = window.location.hostname;
      const pathname = window.location.pathname;
      const isBusinessRoute = pathname.startsWith('/business/');
      if (isBusinessRoute) return true;

      const isRootDomain =
        hostname === 'servicehub.africa' ||
        hostname === 'www.servicehub.africa' ||
        hostname === 'myservicehub.africa' ||
        hostname === 'www.myservicehub.africa' ||
        hostname.includes('localhost') ||
        hostname.includes('gitpod.io') ||
        hostname.includes('run.app') ||
        hostname.includes('vercel.app') ||
        hostname.startsWith('ais-');

      if (!isRootDomain) return true;
      return false;
    } catch {
      return true;
    }
  };

  const [tenant, setTenant] = useState<Business | null>(null);
  const [countryTenant, setCountryTenant] = useState<Tenant | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(initLoading);
  const [urlPrefix, setUrlPrefix] = useState('');
  const location = useLocation();

  const lastResolvedPlatform = React.useRef<string | null>(undefined as any);
  const lastResolvedBusiness = React.useRef<string | null>(undefined as any);

  useEffect(() => {
    const resolveTenant = async () => {
      const pathname = location.pathname;
      const hostname = window.location.hostname;
      
      let businessSlug: string | null = null;
      let platformSlug: string | null = null;
      let currentPrefix = '';

      // 1. Resolve Slugs from Pathname
      const isBusinessRoute = pathname.startsWith('/business/');
      if (isBusinessRoute) {
        const pathParts = pathname.split('/');
        if (pathParts[2]) {
          businessSlug = pathParts[2];
          currentPrefix = `/business/${businessSlug}`;
        }
      }

      // 2. Resolve Slugs from Hostname (Subdomain Multi-tenancy)
      const isRootDomain =
        hostname === 'servicehub.africa' ||
        hostname === 'www.servicehub.africa' ||
        hostname === 'myservicehub.africa' ||
        hostname === 'www.myservicehub.africa' ||
        hostname.includes('localhost') ||
        hostname.includes('gitpod.io') ||
        hostname.includes('run.app') ||
        hostname.includes('vercel.app') ||
        hostname.startsWith('ais-');

      if (!isRootDomain && !businessSlug) {
        const parts = hostname.split('.');
        if (parts.length >= 2 && parts[0] !== 'www') {
          // If we are on a subdomain and don't have a path-based business slug, 
          // use the subdomain as a potential business OR platform slug
          platformSlug = parts[0];
          // We also use it as businessSlug to try resolving business first
          businessSlug = parts[0];
        }
      }

      // OPTIMIZATION: Only resolve if slugs have actually changed
      if (businessSlug === lastResolvedBusiness.current && platformSlug === lastResolvedPlatform.current) {
        // If we were loading, set to false since we already have the right data
        if (loading) setLoading(false);
        return;
      }

      lastResolvedBusiness.current = businessSlug;
      lastResolvedPlatform.current = platformSlug;
      setTenantSlug(businessSlug || platformSlug);

      try {
        // Only set loading if there is a tenant to resolve
        if (businessSlug || platformSlug) {
          setLoading(true);
        }

        // SAFETY TIMEOUT: Force-clear loading after 8s (reduced from 12s)
        const tenantSafetyId = setTimeout(() => {
          setLoading(false);
        }, 8000);

        // Fetch Platform/Country Tenant
        let currentCountryTenant: Tenant | null = null;
        if (platformSlug) {
          const { data: pData } = await supabase
            .from('tenants')
            .select('*')
            .eq('slug', platformSlug)
            .maybeSingle();
          currentCountryTenant = pData;
          setCountryTenant(pData);
        } else {
          setCountryTenant(null);
        }

        // Apply colors to CSS variables
        const root = document.documentElement;
        if (currentCountryTenant?.primary_color) {
          root.style.setProperty('--primary', currentCountryTenant.primary_color);
          root.style.setProperty('--secondary', currentCountryTenant.secondary_color || '#0f172a');
        } else {
          // Default fallback (MyServiceHub Africa Brand)
          root.style.setProperty('--primary', '#2563eb');
          root.style.setProperty('--secondary', '#0f172a');
        }

        // Fetch Business Storefront
        if (businessSlug) {
          const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(businessSlug);
          let bQuery = supabase.from('businesses').select('*');
          if (isUuid) {
            bQuery = bQuery.or(`slug.eq.${businessSlug},id.eq.${businessSlug}`);
          } else {
            bQuery = bQuery.eq('slug', businessSlug);
          }
          const { data: bData } = await bQuery.maybeSingle();
          const cachedBusiness = !navigator.onLine ? getCachedBusiness(businessSlug) : null;
          const resolvedBusiness = bData || cachedBusiness;
          
          if (resolvedBusiness && ((resolvedBusiness as any).deleted_at || (resolvedBusiness as any).is_test_data === true)) {
            setTenant(null);
            setUrlPrefix('');
          } else {
            setTenant(resolvedBusiness as Business);
            if (bData) cachePublicBusiness(bData);
            setUrlPrefix(currentPrefix);
            if ((resolvedBusiness as any)?.primary_color) {
              root.style.setProperty('--primary', (resolvedBusiness as any).primary_color);
              if ((resolvedBusiness as any).secondary_color) {
                root.style.setProperty('--secondary', (resolvedBusiness as any).secondary_color);
              }
            }
          }
        } else {
          setTenant(null);
          setUrlPrefix('');
        }
        
        clearTimeout(tenantSafetyId);
      } catch (err) {
        console.error('[TENANT RESOLVE] Unexpected error:', err);
      } finally {
        setLoading(false);
      }
    };

    resolveTenant();
  }, [location.pathname]);

  return (
    <TenantContext.Provider value={{ 
      tenant, 
      countryTenant,
      tenantSlug, 
      isTenant: !!tenant || !!countryTenant, 
      isStorefront: !!tenant, 
      loading, 
      urlPrefix 
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
